import { Component, h, Prop, State, Listen, forceUpdate } from '@stencil/core';
import { gettrackmeEventCategory, gettrackmeEventObject } from '../../../utils/tracking-helpers';
import store from '../sew-ws-header/store/store';

type EventItem = {
    sourceProject: string;
    isGlobal: boolean;
    date: string;
    description: string;
    endDate: string;
    endDateIso: string;
    id: string;
    location: string;
    past: boolean;
    briefDescription: string;
    startDate: string;
    startDateIso: string;
    eventName: string;
    url: string;
    eventMonth: string;
    eventYear: string;
    eventContextId: string;
    eventTypeId: string;
    moreInformationLink: string;
};

@Component({
    tag: 'sew-ws-events-api',
    shadow: false,
})
export class SewWsEventsApi {
    @Prop() url: string;
    @Prop() sourceProject: string;
    @Prop() headline: string;
    @Prop() textLocal: string;
    @Prop() textGlobal: string;
    @Prop() showMax = 3;
    @Prop() local: boolean;
    @Prop() global: boolean;
    @Prop() eventCalendarUrl: string;
    @Prop() contextFilter: string = null;
    @Prop() typeFilter: string = null;

    @Prop({ reflect: true }) noGrid = false;
    @Prop({ reflect: true }) noMargin = false;

    @State() events: EventItem[];

    private tabsElement: HTMLSewWsTabsElement;

    @Listen('labelsChange', { target: 'window' })
    onLabelsChange(e: CustomEvent<{ [key: string]: string }>) {
        this.textGlobal = e.detail.CMS_EVENT_CALENDAR_GLOBAL;
        this.textLocal =
            e.detail['CMS_EVENT_COUNTRY_DISPLAYNAME_' + store.get('domainInfo')?.country?.toUpperCase()] ??
            e.detail.CMS_EVENT_CALENDAR_LOCAL;

        forceUpdate(this.tabsElement);
    }

    componentWillLoad(): void {
        const now = new Date();
        now.setMinutes(0, 0, 0); // Zero out minutes, seconds, and milliseconds to benefit from caching
        const isoDateNow = now.toISOString();
        fetch(this.url + '&filter={"endDateIso": {"$gte": "' + isoDateNow + '"}}&sort=startDateIso')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                return response.json();
            })
            .then(json => {
                const allEvents: EventItem[] = json._embedded ? json._embedded['rh:doc'] : [];
                if (this.typeFilter) {
                    // if type filter is set, we ignore the global events and only show the local events
                    this.global = false;
                    this.applyContextAndTypeFilter(allEvents);
                } else {
                    this.events = allEvents;
                }
            })
            .catch(e => {
                console.error(e);
            });
    }

    private applyContextAndTypeFilter(allEvents: EventItem[]) {
        const localEvents = allEvents.filter(item => item.sourceProject === this.sourceProject);
        const filteredByType = localEvents.filter(item => item.eventTypeId === this.typeFilter);
        if (filteredByType.length == 0) {
            this.events = localEvents;
        } else {
            if (!this.contextFilter) {
                this.events = filteredByType;
            } else {
                const filteredByContextAndType = filteredByType.filter(
                    item => item.eventContextId === this.contextFilter
                );
                if (filteredByContextAndType.length > 0) {
                    this.events = filteredByContextAndType;
                } else {
                    this.events = filteredByType;
                }
            }
        }
    }

    private getDate(startDateIso: string, endDateIso: string) {
        const options: Intl.DateTimeFormatOptions = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
        };

        if (startDateIso === endDateIso) {
            return `${new Date(startDateIso).toLocaleDateString(
                (window as any).cmsLocale ?? undefined,
                options
            )}`;
        } else {
            return `${new Date(startDateIso).toLocaleDateString(
                (window as any).cmsLocale ?? undefined,
                options
            )} - ${new Date(endDateIso).toLocaleDateString((window as any).cmsLocale ?? undefined, options)}`;
        }
    }

    private getTabItem(tabname: string, events: EventItem[], global: boolean) {
        return (
            <sew-ws-tabs-item
                name={tabname}
                data-sew-trackme-event-category={gettrackmeEventCategory('Events')}
                data-sew-trackme-event-object={gettrackmeEventObject('Tab_' + tabname)}
                data-sew-trackme-event-action="Click"
                button-href={!global ? '#nat' : '#int'}
            >
                <sew-ws-events no-grid no-margin class="inside-tab">
                    {events
                        .filter(item => {
                            // Lokal zeigt alle Events aus dem passenden sourceProject an
                            // Global zeigt alle Events an mit isGlobal=true
                            // Das heißt, dass es auch Events geben kann die in beiden Tabs auftauchen, nämlich die,
                            // die aus dem sourceProject kommen und als global geflaggt sind
                            if (global) {
                                return item.isGlobal;
                            } else {
                                return item.sourceProject === this.sourceProject;
                            }
                        })
                        .slice(0, this.showMax)
                        .map(item => {
                            return (
                                <sew-ws-events-item
                                    location={item.location}
                                    date={this.getDate(item.startDateIso, item.endDateIso)}
                                >
                                    <a
                                        // link to events overview page and show event there
                                        // href={
                                        //     this.eventCalendarUrl +
                                        //     (!global ? '#nat/' : '#int/') +
                                        //     item.eventMonth
                                        // }

                                        // link to the event detail page
                                        href={item.moreInformationLink}
                                        data-sew-trackme-event-category={gettrackmeEventCategory('Event')}
                                        data-sew-trackme-event-object={gettrackmeEventObject(item.eventName)}
                                        data-sew-trackme-event-action="Click"
                                    >
                                        {item.eventName}
                                    </a>
                                </sew-ws-events-item>
                            );
                        })}
                </sew-ws-events>
            </sew-ws-tabs-item>
        );
    }

    render() {
        return (
            this.events && (
                <sew-ws-tabs
                    ref={el => (this.tabsElement = el)}
                    headline={this.headline}
                    no-grid={this.noGrid}
                    no-margin={this.noMargin}
                >
                    {this.local && this.getTabItem(this.textLocal, this.events, false)}
                    {this.global && this.getTabItem(this.textGlobal, this.events, true)}
                    <slot name="button"></slot>
                </sew-ws-tabs>
            )
        );
    }
}
